'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { X, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { useCambiarPassword } from '@/components/hooks/useCambiarPassword';
import {
  DLG_BTN_PRIMARY,
  DLG_BTN_SECONDARY,
} from '@/components/ui/dialogButtons';
import { PASSWORD_MIN, passwordValida } from '@/components/utils/password';
import PasswordRequisitos from '@/components/ui/PasswordRequisitos';

const ChangePasswordDialog = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    newPassword,
    confirmPassword,
    loading,
    setNewPassword,
    setConfirmPassword,
    cambiar,
    reset,
  } = useCambiarPassword();

  const passwordOK =
    passwordValida(newPassword) && newPassword === confirmPassword;

  const cerrar = () => {
    setOpen(false);
    reset();
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const handleSave = async () => {
    // Validar antes de llamar a Supabase: si no, el rechazo llega como error genérico
    if (!passwordValida(newPassword)) {
      toast.error(t('password.errorWeak', { min: PASSWORD_MIN }));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(t('password.errorMatch'));
      return;
    }

    const ok = await cambiar();
    if (ok) cerrar();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => (v ? setOpen(true) : cerrar())}
    >
      <DialogTrigger asChild>
        <Button className="w-full sm:w-auto rounded-lg font-medium shadow-md bg-[#103254] hover:bg-[#0d2742] dark:bg-[#103254] dark:hover:bg-[#0d2742] text-white transition-colors">
          {t('changePasswordDialog.trigger')}
        </Button>
      </DialogTrigger>

      <DialogContent
        className="
          z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
          [&>button:last-child]:hidden
          flex flex-col
          !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
          w-full max-w-none max-h-[85vh] rounded-t-2xl rounded-b-none
          data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
          sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
          sm:w-[95%] sm:max-w-lg sm:mx-auto sm:max-h-[85vh] sm:rounded-xl sm:shadow-xl
        "
      >
        {/* Header con X */}
        <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0 relative">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-semibold text-[#103254] dark:text-blue-400 pr-10">
              {t('changePasswordDialog.title')}
            </DialogTitle>

            <DialogDescription className="text-sm text-gray-500 dark:text-gray-400">
              {t('changePasswordDialog.description')}
            </DialogDescription>
          </DialogHeader>
          <button
            onClick={cerrar}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-5">
          <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4 space-y-4">
            <div className="space-y-1">
              <Label
                htmlFor="newPassword"
                className="text-sm text-gray-700 dark:text-gray-300"
              >
                {t('changePasswordDialog.newPassword')}
              </Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder={t('password.placeholder')}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="rounded-md pr-10 border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-white/40 hover:text-gray-600 dark:hover:text-white/70"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
              <PasswordRequisitos password={newPassword} />
            </div>

            <div className="space-y-1">
              <Label
                htmlFor="confirmPassword"
                className="text-sm text-gray-700 dark:text-gray-300"
              >
                {t('changePasswordDialog.confirmPassword')}
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="rounded-md pr-10 border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  tabIndex={-1}
                  aria-label={
                    showConfirmPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'
                  }
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-white/40 hover:text-gray-600 dark:hover:text-white/70"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
              {confirmPassword.length > 0 && newPassword !== confirmPassword && (
                <p className="text-xs text-red-500">
                  {t('password.errorMatch')}
                </p>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="px-4 sm:px-6 pt-2 pb-5 sm:pb-4 flex flex-col sm:flex-row gap-3 shrink-0 border-t border-gray-100 dark:border-white/10">
          <Button
            variant="outline"
            onClick={cerrar}
            disabled={loading}
            className={`w-full sm:flex-1 ${DLG_BTN_SECONDARY}`}
          >
            {t('changePasswordDialog.cancel')}
          </Button>

          <Button
            onClick={handleSave}
            disabled={loading || !passwordOK}
            className={`w-full sm:flex-1 ${DLG_BTN_PRIMARY}`}
          >
            {loading
              ? t('changePasswordDialog.saving')
              : t('changePasswordDialog.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePasswordDialog;